// analysisQueue.js — thin client for the BACKEND analysis queue.
//
// The queue itself lives in the Node backend (see the analysisQueue
// routes + worker pasted into the server's common routes file): jobs are
// stored in the `analysis_queue` Mongo collection and a worker inside the
// backend runs them strictly one at a time, with retry + stall detection.
// That way the queue keeps running when the browser closes, and the
// frequent SSH/start failures are retried server-side.
//
// Backend API this module talks to:
//   POST   api/common/analysisQueue/add            {userid,name,codeType,server,startdate,enddate}
//   GET    api/common/analysisQueue/list           → { items: [...] }
//   POST   api/common/analysisQueue/retry/:id
//   POST   api/common/analysisQueue/clearFinished
//   DELETE api/common/analysisQueue/:id
//
// Consumers:
//   AddModal.js / AddModalComplete.js — "Queue" button → enqueue()
//   AnalysisDashboard.js              — subscribe((items, error) => …)

import axios from "axios";
import { apiUrl } from "../../config/config";

export const CODE_LABELS = {
  "first-code": "FULL AI",
  "second-code": "CLASSIC AI",
  "third-code": "HYBRID AI",
  "fourth-code": "NEGPEAK AI",
};

const POLL_MS = 4000;

let subscribers = [];
let timer = null;
let lastItems = [];
let lastError = null;

// Backend status → UI status. A re-queued job (failed start, waiting out
// its back-off) shows as "retrying" so the dashboard tells the truth.
function mapDoc(d) {
  let status = { queued: "waiting", running: "running", done: "done", failed: "failed" }[d.status] || d.status;
  if (d.status === "queued" && (d.attempts || 0) > 0) status = "retrying";
  return {
    qid: d._id,
    userid: d.userid,
    name: d.name || d.userid,
    codeType: d.codeType,
    server: d.server,
    status,
    progress: d.progress || 0,
    error: d.error || null,
    attempts: d.attempts || 0,
    addedAt: d.createdAt,
  };
}

function notify() {
  subscribers.forEach((fn) => {
    try { fn(lastItems.map((i) => ({ ...i })), lastError); } catch (e) { /* subscriber errors are theirs */ }
  });
}

async function fetchList() {
  try {
    const res = await axios.get(`${apiUrl}api/common/analysisQueue/list`);
    lastItems = ((res.data && res.data.items) || []).map(mapDoc);
    lastError = null;
  } catch (err) {
    // Surface the backend problem — do not hide it (missing routes, server
    // down, CORS). The dashboard shows this as a banner.
    lastError = (err && err.message) || "queue backend unreachable";
  }
  notify();
}

export function getQueue() {
  return lastItems.map((i) => ({ ...i }));
}

// fn is called with (items, error). Polling runs only while someone is
// subscribed (i.e. the Analysis Dashboard is open).
export function subscribe(fn) {
  subscribers.push(fn);
  fn(lastItems.map((i) => ({ ...i })), lastError);
  if (!timer) {
    fetchList();
    timer = setInterval(fetchList, POLL_MS);
  }
  return () => {
    subscribers = subscribers.filter((f) => f !== fn);
    if (!subscribers.length && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

// Resolves true when queued, false when the patient already has a pending
// entry. Rejects when the backend is unreachable — callers show the error.
export async function enqueue(item) {
  const res = await axios.post(`${apiUrl}api/common/analysisQueue/add`, item);
  fetchList();
  return !!(res.data && res.data.added);
}

export async function enqueueMany(items) {
  let added = 0;
  for (const it of items || []) {
    try { if (await enqueue(it)) added++; } catch (e) { /* counted as not added */ }
  }
  return added;
}

async function safeCall(call) {
  try {
    await call();
  } catch (err) {
    lastError = (err && err.message) || "queue backend unreachable";
  }
  fetchList();
}

export function removeItem(qid) {
  return safeCall(() => axios.delete(`${apiUrl}api/common/analysisQueue/${qid}`));
}

export function retryItem(qid) {
  return safeCall(() => axios.post(`${apiUrl}api/common/analysisQueue/retry/${qid}`));
}

export function clearFinished() {
  return safeCall(() => axios.post(`${apiUrl}api/common/analysisQueue/clearFinished`));
}
